import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/apiClient";
import { Navbar } from "../components/Navbar";
import { VideoCard } from "../components/VideoCard";
import { useAuth } from "../context/AuthContext";
import { useMyList } from "../context/MyListContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

// Swipe distance (px) past which releasing removes the item.
const SWIPE_REMOVE_THRESHOLD = 110;

// Wraps a card so it can be swiped left (touch) to remove, with a visible
// remove button for pointer devices.
const SwipeItem = ({ video, onRemove }) => {
  const [dx, setDx] = useState(0);
  const startX = useRef(null);

  const onTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
  };
  const onTouchMove = (e) => {
    if (startX.current == null) return;
    const delta = e.touches[0].clientX - startX.current;
    if (delta < 0) setDx(Math.max(delta, -180));
  };
  const onTouchEnd = () => {
    if (dx < -SWIPE_REMOVE_THRESHOLD) onRemove();
    setDx(0);
    startX.current = null;
  };

  return (
    <div className="relative overflow-hidden rounded-sm" data-testid={`mylist-item-${video.id}`}>
      <div className="absolute inset-y-0 right-0 flex items-center gap-1.5 pr-4 text-[#FF2A2A]">
        <Trash2 className="w-4 h-4" />
        <span className="text-xs uppercase tracking-wide">Remove</span>
      </div>
      <div
        style={{ transform: `translateX(${dx}px)` }}
        className={dx === 0 ? "transition-transform duration-200" : ""}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="relative">
          <VideoCard video={video} hideSave />
          <button
            type="button"
            data-testid={`mylist-remove-${video.id}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemove();
            }}
            aria-label="Remove from My List"
            className="absolute top-2 left-2 z-30 w-8 h-8 rounded-full bg-black/70 backdrop-blur border border-white/25 flex items-center justify-center hover:bg-[#FF2A2A] hover:border-[#FF2A2A] transition-colors"
          >
            <X className="w-4 h-4 text-white" strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default function MyList() {
  const { user } = useAuth();
  const { toggle, clearAll } = useMyList();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/mylist")
      .then((r) => setVideos(r.data.videos || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const removeOne = (v) => {
    setVideos((prev) => prev.filter((x) => x.id !== v.id));
    toggle(v.id);
    toast(`Removed “${v.title}”`);
  };

  const handleClearAll = () => {
    setVideos([]);
    clearAll();
    toast("Cleared My List");
  };

  return (
    <div className="min-h-screen bg-[#030305]">
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-28 md:pt-32 pb-24">
        <p className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Saved by {user?.name}</p>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
          <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-tighter">My List</h1>
          {videos.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  data-testid="mylist-clear-all"
                  className="flex items-center gap-2 text-sm border border-white/20 hover:border-[#FF2A2A] hover:text-white text-zinc-300 px-4 py-2 rounded-sm transition-colors"
                >
                  <Trash2 className="w-4 h-4" /> Clear All
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#0A0A0F] border-white/15">
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear your entire list?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This removes all {videos.length} saved show{videos.length !== 1 ? "s" : ""} from My List. You can always add them again later.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="mylist-clear-cancel" className="bg-transparent border-white/20 hover:bg-white/10">
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="mylist-clear-confirm"
                    onClick={handleClearAll}
                    className="bg-[#FF2A2A] hover:bg-[#FF4D4D] text-white"
                  >
                    Clear All
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#FF2A2A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : videos.length === 0 ? (
          <div className="border border-white/10 rounded-sm p-16 text-center" data-testid="mylist-empty">
            <p className="text-zinc-400 mb-6">Your list is empty. Tap the + on any show to save it here.</p>
            <Link
              to="/channels"
              className="inline-flex bg-[#FF2A2A] hover:bg-[#FF4D4D] text-white px-6 py-3 rounded-sm font-semibold transition-colors"
            >
              Browse Channels
            </Link>
          </div>
        ) : (
          <>
            <p className="text-xs text-zinc-600 mb-4 md:hidden">Tip: swipe a show left to remove it.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="mylist-grid">
              {videos.map((v) => (
                <SwipeItem key={v.id} video={v} onRemove={() => removeOne(v)} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
